// Profile photos in Cloud Storage for Firebase.
// Stored at betaCommunityUsers/{uid}/avatar.jpg as a 512×512 JPEG (centre-cropped).
// Needs `export const storage = getStorage(app)` in ../firebase.js.

import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';
import { updateProfile } from './userService';

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // before resizing
const SIDE = 512;

/** Message safe to show the user. */
export class PhotoError extends Error {}

/** Centre-crop any image Blob to a square and re-encode as JPEG. */
export async function toSquareJpeg(blob, side = SIDE) {
  const bitmap = await createImageBitmap(blob);
  const crop = Math.min(bitmap.width, bitmap.height);
  const out = Math.min(side, crop);
  const canvas = document.createElement('canvas');
  canvas.width = out;
  canvas.height = out;
  canvas
    .getContext('2d')
    .drawImage(bitmap, (bitmap.width - crop) / 2, (bitmap.height - crop) / 2, crop, crop, 0, 0, out, out);
  if (bitmap.close) bitmap.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('JPEG encode failed'))), 'image/jpeg', 0.86)
  );
}

async function uploadAvatar(uid, jpeg) {
  const fileRef = ref(storage, `betaCommunityUsers/${uid}/avatar.jpg`);
  await uploadBytes(fileRef, jpeg, { contentType: 'image/jpeg', cacheControl: 'public, max-age=3600' });
  const url = await getDownloadURL(fileRef);
  // Same path every time, so add a version to the URL; otherwise browsers keep showing the old photo.
  return `${url}&v=${Date.now()}`;
}

/** "Change photo": resize, upload, and save the new URL on the profile. Returns the URL. */
export async function uploadProfilePhoto(uid, file) {
  if (!file || !file.type.startsWith('image/')) {
    throw new PhotoError('Choose an image file, such as a JPG, PNG or WebP.');
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new PhotoError('That photo is over 10 MB. Choose a smaller one.');
  }
  let jpeg;
  try {
    jpeg = await toSquareJpeg(file);
  } catch (err) {
    // e.g. HEIC photos in browsers that can't decode them
    throw new PhotoError('We couldn’t read that image. Try a JPG or PNG.');
  }
  const url = await uploadAvatar(uid, jpeg);
  await updateProfile(uid, { photoUrl: url, photoSource: 'upload' });
  return url;
}

/**
 * Copy a Google profile photo into Storage so the profile no longer depends on Google's URL.
 * Best effort: if the download is blocked (e.g. by CORS), it keeps the Google URL and returns null.
 */
export async function copyGooglePhoto(uid, googleUrl) {
  if (!googleUrl || !/googleusercontent\.com/.test(googleUrl)) return null;
  try {
    const bigger = googleUrl.replace(/=s\d+(-c)?$/, '=s512-c'); // Google's default is a 96px thumbnail
    const res = await fetch(bigger, { referrerPolicy: 'no-referrer' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const url = await uploadAvatar(uid, await toSquareJpeg(await res.blob()));
    await updateProfile(uid, { photoUrl: url, photoSource: 'google' });
    return url;
  } catch (err) {
    console.warn('Could not copy the Google photo into Storage; keeping the Google URL.', err);
    return null;
  }
}
